/**
 * Het mailvak: Aan (chip met naam), CC en BCC, Onderwerp, bijlagen als
 * chip en de tekst met werkbalk (sessie 72, punt 9). Eén component voor
 * de mailstand en voor Beslissen met kanaal E-mail; er is geen tweede
 * variant. De stand zelf bepaalt de hoofdknop (Versturen of Akkoord en
 * versturen) en de route (`versturen` of `perMail`); dit vak levert alleen
 * de velden en wat er naar de server gaat.
 */
import { useState, type ReactNode } from "react";

import { AdresVeld, BijlageChips, type BijlageStand } from "./vak";
import { MailTekst } from "./mailtekst";

export interface MailAdressen {
  aan: string[];
  cc: string[];
  bcc: string[];
}

export interface MailVeldenStand {
  aan: string[];
  zetAan: (adressen: string[]) => void;
  cc: string[];
  zetCc: (adressen: string[]) => void;
  bcc: string[];
  zetBcc: (adressen: string[]) => void;
  ccOpen: boolean;
  zetCcOpen: (open: boolean) => void;
  bccOpen: boolean;
  zetBccOpen: (open: boolean) => void;
  /** Wat naar de server gaat: een dichtgeklapt CC- of BCC-veld telt niet. */
  adressen: () => MailAdressen;
}

/**
 * De adressen van het mailvak. `actief`: staat het vak nu open? Beslissen
 * houdt zijn stand bij een kanaalwissel; gaat het mailvak daar (weer) open,
 * dan begint het met de adressen van dat moment (Aan uit de kanaalkeuze).
 */
export function useMailVelden(begin: MailAdressen, actief = true): MailVeldenStand {
  const [aan, zetAan] = useState<string[]>(begin.aan);
  const [cc, zetCc] = useState<string[]>(begin.cc);
  const [bcc, zetBcc] = useState<string[]>(begin.bcc);
  const [ccOpen, zetCcOpen] = useState(begin.cc.length > 0);
  const [bccOpen, zetBccOpen] = useState(begin.bcc.length > 0);
  const [wasActief, zetWasActief] = useState(actief);
  if (actief !== wasActief) {
    zetWasActief(actief);
    if (actief) {
      zetAan(begin.aan);
      zetCc(begin.cc);
      zetBcc(begin.bcc);
      zetCcOpen(begin.cc.length > 0);
      zetBccOpen(begin.bcc.length > 0);
    }
  }
  return {
    aan,
    zetAan,
    cc,
    zetCc,
    bcc,
    zetBcc,
    ccOpen,
    zetCcOpen,
    bccOpen,
    zetBccOpen,
    adressen: () => ({ aan, cc: ccOpen ? cc : [], bcc: bccOpen ? bcc : [] }),
  };
}

export function MailVelden({
  velden,
  naam,
  onderwerp,
  onOnderwerp,
  tekst,
  onTekst,
  bijlagen,
  tekstLabel,
  markeer,
  onder,
}: {
  velden: MailVeldenStand;
  /** Naam van de klant voor de Aan-chip ("Naam, adres"). */
  naam: string | null;
  onderwerp: string;
  onOnderwerp: (onderwerp: string) => void;
  tekst: string;
  onTekst: (tekst: string) => void;
  bijlagen: BijlageStand;
  /** Naam van het tekstvak voor schermlezers (standaard "Antwoord"). */
  tekstLabel?: string;
  /** Motor 7 (keuze 9): lijstzinnen met een lijntje. */
  markeer?: readonly string[];
  /** Motor 7 (keuze 9): direct onder de tekst, zoals de handtekening. */
  onder?: ReactNode;
}) {
  const { ccOpen, bccOpen, zetCcOpen, zetBccOpen } = velden;
  return (
    <>
      <AdresVeld
        label="Aan"
        adressen={velden.aan}
        onAdressen={velden.zetAan}
        naam={naam}
        rechts={
          <>
            <button
              type="button"
              className={`bureau${ccOpen ? " on" : ""}`}
              onClick={() => zetCcOpen(!ccOpen)}
            >
              CC
            </button>
            <button
              type="button"
              className={`bureau${bccOpen ? " on" : ""}`}
              onClick={() => zetBccOpen(!bccOpen)}
            >
              BCC
            </button>
            {/* iPhone: CC en BCC achter één knop (ontwerp 26). */}
            <button
              type="button"
              className={`mob${ccOpen || bccOpen ? " on" : ""}`}
              aria-expanded={ccOpen && bccOpen}
              onClick={() => {
                const open = !(ccOpen && bccOpen);
                zetCcOpen(open);
                zetBccOpen(open);
              }}
            >
              CC/BCC
            </button>
          </>
        }
      />
      {ccOpen && <AdresVeld label="CC" adressen={velden.cc} onAdressen={velden.zetCc} />}
      {bccOpen && <AdresVeld label="BCC" adressen={velden.bcc} onAdressen={velden.zetBcc} />}
      <div className="rij">
        <span className="l">Onderwerp</span>
        <input aria-label="Onderwerp" value={onderwerp} onChange={(e) => onOnderwerp(e.target.value)} />
        <span />
      </div>
      <BijlageChips bijlagen={bijlagen} />
      <MailTekst
        waarde={tekst}
        onWaarde={onTekst}
        onAfbeelding={() => bijlagen.kies("image/*")}
        label={tekstLabel}
        markeer={markeer}
        onder={onder}
      />
    </>
  );
}
